import { CLASES_TONO, type Tono } from "../vocabulario.ts";
import { cx } from "./ui.tsx";

export interface Porcion {
  clave: string;
  valor: number;
  texto: string;
  tono: Tono;
}

/**
 * Rosca de composición, dibujada a mano.
 *
 * Una librería de gráficos entera para un solo anillo sería más peso que el
 * resto del panel junto. Son cuatro arcos y un `stroke-dasharray`.
 */
export function Rosca({
  porciones,
  centro,
  pie,
  tamano = 148,
  grosor = 14,
}: {
  porciones: Porcion[];
  centro?: string;
  pie?: string;
  tamano?: number;
  grosor?: number;
}) {
  const total = porciones.reduce((s, p) => s + p.valor, 0);
  const radio = (tamano - grosor) / 2;
  const circunferencia = 2 * Math.PI * radio;

  let acumulado = 0;
  const arcos = porciones
    .filter((p) => p.valor > 0)
    .map((p) => {
      const largo = total ? (p.valor / total) * circunferencia : 0;
      const desfase = acumulado;
      acumulado += largo;
      return { ...p, largo, desfase };
    });

  return (
    <div className="flex flex-wrap items-center gap-6">
      <div className="relative shrink-0" style={{ width: tamano, height: tamano }}>
        <svg width={tamano} height={tamano} className="-rotate-90" role="img" aria-hidden>
          <circle
            cx={tamano / 2}
            cy={tamano / 2}
            r={radio}
            fill="none"
            stroke="var(--color-superficie-2)"
            strokeWidth={grosor}
          />
          {arcos.map((a) => (
            <circle
              key={a.clave}
              cx={tamano / 2}
              cy={tamano / 2}
              r={radio}
              fill="none"
              strokeWidth={grosor}
              strokeLinecap="round"
              className={cx("transition-all duration-700", CLASES_TONO[a.tono].tinte)}
              stroke="currentColor"
              strokeDasharray={`${Math.max(0, a.largo - 3)} ${circunferencia}`}
              strokeDashoffset={-a.desfase}
            />
          ))}
        </svg>
        {centro && (
          <div className="absolute inset-0 grid place-items-center text-center">
            <div>
              <p className="cifra text-2xl font-semibold tracking-tight">{centro}</p>
              {pie && <p className="mt-0.5 text-[11px] text-tenue">{pie}</p>}
            </div>
          </div>
        )}
      </div>

      <ul className="min-w-40 flex-1 space-y-2.5">
        {porciones.map((p) => (
          <li key={p.clave} className="flex items-center gap-2.5 text-sm">
            <span
              className={cx("size-2.5 shrink-0 rounded-full", CLASES_TONO[p.tono].punto)}
              aria-hidden
            />
            <span className="flex-1 truncate text-tenue">{p.texto}</span>
            <span className="cifra font-medium">{p.valor}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
